import React, { useEffect, useState } from 'react';
import { posAPI, POSOpenInvoice, POSInvoiceLineItem } from '../../services/storeAPI';

// Read-only "what this invoice is for" readout — shared by the list
// rows and the confirm step. Display context only: never affects the
// amount collected.
export const invoiceLineDetail = (li: POSInvoiceLineItem): string => {
  const parts: string[] = [];
  if (li.quantity_lbs > 0) parts.push(`${li.quantity_lbs.toLocaleString()} lbs`);
  if (li.packages > 0) parts.push(`${li.packages} ${li.packages === 1 ? 'bag' : 'bags'}`);
  return parts.join(' / ');
};

const InvoiceLineItems: React.FC<{ items: POSInvoiceLineItem[] }> = ({ items }) => {
  if (!items || items.length === 0) {
    return <div style={lineStyles.empty}>no line detail</div>;
  }
  return (
    <div style={lineStyles.wrap}>
      {items.map((li, i) => (
        <div key={i} style={lineStyles.row}>
          <span style={lineStyles.name}>
            {li.name}
            {invoiceLineDetail(li) && (
              <span style={lineStyles.qty}> — {invoiceLineDetail(li)}</span>
            )}
          </span>
          <span style={lineStyles.total}>${li.line_total.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

const lineStyles: { [key: string]: React.CSSProperties } = {
  wrap: {
    marginTop: 6, paddingTop: 6, borderTop: '1px dashed #E5E7EB',
  },
  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: 8, fontSize: 12, color: '#4B5563', padding: '1px 0',
  },
  name: { textAlign: 'left' },
  qty: { color: '#6B7280' },
  total: { whiteSpace: 'nowrap', color: '#4B5563' },
  empty: { marginTop: 6, fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' },
};

/**
 * POS invoice-collection modal (A1).
 *
 * A deliberate SECONDARY step after a customer is selected at the till:
 * lists the customer's open invoices (balance due > 0), then a second
 * confirm step where the cashier can edit the collection amount DOWN
 * from the full balance (partial payment) and attach an optional note.
 * The invoice's own line items are never shown as editable — this is a
 * payment against the balance, not invoice editing.
 *
 * BC members resolve through member.customer on the backend; an
 * unlinked member gets a clear "not linked to a billing account" state.
 */

interface Props {
  memberId?: number | null;
  customerId?: number | null;
  customerName: string;
  onApply: (invoiceId: number, amount: number, note: string) => Promise<void>;
  onClose: () => void;
}

const POSInvoicePanel: React.FC<Props> = ({ memberId, customerId, customerName, onApply, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [notLinkedMessage, setNotLinkedMessage] = useState('');
  const [invoices, setInvoices] = useState<POSOpenInvoice[]>([]);
  const [selected, setSelected] = useState<POSOpenInvoice | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const params = memberId ? { member_id: memberId } : { customer_id: customerId! };
        const response = await posAPI.getOpenInvoices(params);
        if (response.linked === false) {
          setNotLinkedMessage(response.message || 'This member is not linked to a billing account.');
          setInvoices([]);
        } else {
          setInvoices(response.invoices || []);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [memberId, customerId]);

  const selectInvoice = (inv: POSOpenInvoice) => {
    setSelected(inv);
    setAmount(inv.balance_due); // default: collect the FULL balance
    setNote('');
    setError('');
  };

  const amountError = (): string => {
    if (!selected) return '';
    const amt = parseFloat(amount);
    const balance = parseFloat(selected.balance_due);
    if (isNaN(amt) || amt <= 0) return 'Enter an amount greater than $0.00';
    if (amt > balance) return `Cannot exceed the balance due ($${balance.toFixed(2)})`;
    return '';
  };

  const isPartial = selected
    ? parseFloat(amount || '0') > 0 && parseFloat(amount || '0') < parseFloat(selected.balance_due)
    : false;

  const handleApply = async () => {
    if (!selected || amountError()) return;
    try {
      setApplying(true);
      setError('');
      await onApply(selected.id, parseFloat(amount), note.trim());
    } catch (err: any) {
      setError(err.message || 'Failed to add invoice payment');
      setApplying(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {selected ? `Collect on ${selected.invoice_number}` : `Open Invoices — ${customerName}`}
          </h3>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <p style={styles.muted}>Loading invoices…</p>
        ) : notLinkedMessage ? (
          <div style={styles.notLinked}>
            <span style={{ fontSize: 28 }}>🔗</span>
            <p style={{ margin: '8px 0 0' }}>{notLinkedMessage}</p>
          </div>
        ) : !selected ? (
          invoices.length === 0 ? (
            <p style={styles.muted}>No open invoices — this account is settled up. 🎉</p>
          ) : (
            <div>
              <p style={styles.hint}>
                Pick the invoice to collect on. Amount owed is the <strong>balance due</strong>, not the order total.
              </p>
              {invoices.map((inv) => (
                <button key={inv.id} style={styles.invoiceRow} onClick={() => selectInvoice(inv)}>
                  <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div>
                        <div style={styles.invoiceNumber}>
                          {inv.invoice_number}
                          {inv.status === 'partial' && <span style={styles.partialBadge}>PARTIAL</span>}
                          {inv.is_overdue && <span style={styles.overdueBadge}>OVERDUE</span>}
                        </div>
                        <div style={styles.invoiceMeta}>
                          {inv.order_number ? `Order ${inv.order_number} · ` : ''}
                          {inv.invoice_date || ''} · total ${parseFloat(inv.total_amount).toFixed(2)}
                        </div>
                      </div>
                      <div style={styles.balanceCol}>
                        <div style={styles.balanceAmount}>${parseFloat(inv.balance_due).toFixed(2)}</div>
                        <div style={styles.balanceLabel}>balance due</div>
                      </div>
                    </div>
                    <InvoiceLineItems items={inv.line_items || []} />
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          <div>
            <div style={{ ...styles.summaryBox, display: 'block' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={styles.invoiceMeta}>
                    {selected.order_number ? `Order ${selected.order_number} · ` : ''}invoice total $
                    {parseFloat(selected.total_amount).toFixed(2)}
                  </div>
                  <div style={{ fontWeight: 700, marginTop: 2 }}>
                    Balance due: ${parseFloat(selected.balance_due).toFixed(2)}
                  </div>
                </div>
                <button style={styles.backBtn} onClick={() => setSelected(null)}>← All invoices</button>
              </div>
              <InvoiceLineItems items={selected.line_items || []} />
            </div>

            <label style={styles.label}>Amount to collect</label>
            <input
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              max={selected.balance_due}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={styles.amountInput}
              autoFocus
            />
            {amountError() && <div style={styles.fieldError}>{amountError()}</div>}
            {isPartial && !amountError() && (
              <div style={styles.partialNote}>
                Partial payment — ${(parseFloat(selected.balance_due) - parseFloat(amount)).toFixed(2)} will
                remain due and the invoice stays open for the next visit.
              </div>
            )}

            <label style={styles.label}>
              Note {isPartial ? '(encouraged for partial payments)' : '(optional)'}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Paying half today, rest on Friday — per Brad"
              style={styles.noteInput}
              rows={2}
              maxLength={500}
            />

            <button
              onClick={handleApply}
              disabled={!!amountError() || applying}
              style={{
                ...styles.applyBtn,
                ...(amountError() || applying ? styles.applyBtnDisabled : {}),
              }}
            >
              {applying
                ? 'Adding…'
                : `Add $${(parseFloat(amount) || 0).toFixed(2)} payment to cart`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: '#fff', borderRadius: 12, padding: 20, width: 520,
    maxWidth: '94vw', maxHeight: '84vh', overflowY: 'auto',
    boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  title: { margin: 0, fontSize: 18, color: '#1f2937' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
    color: '#6b7280', padding: 4,
  },
  error: {
    background: '#FEE2E2', color: '#B91C1C', padding: '8px 12px',
    borderRadius: 8, marginBottom: 10, fontSize: 14,
  },
  muted: { color: '#6b7280', textAlign: 'center', padding: '18px 0' },
  hint: { color: '#6b7280', fontSize: 13, margin: '0 0 10px' },
  notLinked: {
    textAlign: 'center', padding: '20px 12px', color: '#92400E',
    background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
  },
  invoiceRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', padding: '10px 12px', marginBottom: 8,
    background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8,
    cursor: 'pointer',
  },
  invoiceNumber: { fontWeight: 700, color: '#111827', fontSize: 15 },
  invoiceMeta: { color: '#6b7280', fontSize: 12, marginTop: 2 },
  partialBadge: {
    fontSize: 10, fontWeight: 700, color: '#92400E', background: '#FEF3C7',
    padding: '1px 6px', borderRadius: 3, marginLeft: 6,
  },
  overdueBadge: {
    fontSize: 10, fontWeight: 700, color: '#B91C1C', background: '#FEE2E2',
    padding: '1px 6px', borderRadius: 3, marginLeft: 6,
  },
  balanceCol: { textAlign: 'right' },
  balanceAmount: { fontWeight: 700, fontSize: 17, color: '#B91C1C' },
  balanceLabel: { fontSize: 11, color: '#6b7280' },
  summaryBox: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8,
    padding: '10px 12px', marginBottom: 14,
  },
  backBtn: {
    background: 'none', border: '1px solid #D1D5DB', borderRadius: 6,
    padding: '6px 10px', cursor: 'pointer', color: '#374151', fontSize: 13,
  },
  label: { display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', margin: '10px 0 4px' },
  amountInput: {
    width: '100%', padding: '10px 12px', fontSize: 20, fontWeight: 700,
    border: '2px solid #2D5A27', borderRadius: 8, boxSizing: 'border-box',
  },
  fieldError: { color: '#B91C1C', fontSize: 13, marginTop: 4 },
  partialNote: {
    background: '#FEF3C7', color: '#92400E', fontSize: 13,
    padding: '6px 10px', borderRadius: 6, marginTop: 6,
  },
  noteInput: {
    width: '100%', padding: '8px 10px', fontSize: 14,
    border: '1px solid #D1D5DB', borderRadius: 8, boxSizing: 'border-box',
    resize: 'vertical',
  },
  applyBtn: {
    width: '100%', marginTop: 16, padding: '12px 0', fontSize: 16,
    fontWeight: 700, color: '#fff', background: '#2D5A27',
    border: 'none', borderRadius: 8, cursor: 'pointer',
  },
  applyBtnDisabled: { background: '#9CA3AF', cursor: 'not-allowed' },
};

export default POSInvoicePanel;
